import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MagnifyingGlassIcon,
  PencilSquareIcon,
  XMarkIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import * as mcquizApi from '../services/mcquizApi';

const PAGE_SIZE = 25;

export default function McQuizQuestionBrowser() {
  const queryClient = useQueryClient();

  // Filters
  const [searchText, setSearchText] = useState('');
  const [subject, setSubject] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [bankId, setBankId] = useState('');
  const [page, setPage] = useState(0);

  // Edit modal
  const [editingQuestion, setEditingQuestion] = useState<any | null>(null);
  const [editForm, setEditForm] = useState<any>({});

  // Banks for filter
  const { data: banksData } = useQuery({
    queryKey: ['mcquiz-banks'],
    queryFn: () => mcquizApi.listBanks().then((r) => r.data),
  });
  const banks: any[] = banksData?.banks ?? banksData ?? [];

  // Questions
  const { data, isLoading, isFetching } = useQuery<any>({
    queryKey: ['mcquiz-questions', searchText, subject, difficulty, bankId, page],
    queryFn: () =>
      mcquizApi
        .searchQuestions({
          q: searchText || undefined,
          subject: subject || undefined,
          difficulty: difficulty || undefined,
          bank_id: bankId ? Number(bankId) : undefined,
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
        })
        .then((r) => r.data),
    keepPreviousData: true,
  } as any);

  const questions: any[] = data?.questions ?? data ?? [];
  const total: number = data?.total ?? questions.length;

  // Edit mutation
  const editMutation = useMutation({
    mutationFn: () => mcquizApi.editQuestion(editingQuestion.id, editForm),
    onSuccess: () => {
      toast.success('Question updated');
      setEditingQuestion(null);
      queryClient.invalidateQueries({ queryKey: ['mcquiz-questions'] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.detail || 'Update failed'),
  });

  const openEdit = (q: any) => {
    setEditingQuestion(q);
    setEditForm({
      question_text: q.question_text || q.question || '',
      subject: q.subject || '',
      difficulty: q.difficulty || '',
      is_active: q.is_active !== false,
      explanation: q.explanation || '',
    });
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Question Browser</h1>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar filters */}
        <aside className="lg:w-56 flex-shrink-0 space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Filters</h3>

            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Subject
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => {
                  setSubject(e.target.value);
                  setPage(0);
                }}
                placeholder="e.g. Polity"
                className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1.5 text-xs"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Difficulty
              </label>
              <select
                value={difficulty}
                onChange={(e) => {
                  setDifficulty(e.target.value);
                  setPage(0);
                }}
                className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1.5 text-xs"
              >
                <option value="">All</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Bank
              </label>
              <select
                value={bankId}
                onChange={(e) => {
                  setBankId(e.target.value);
                  setPage(0);
                }}
                className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1.5 text-xs"
              >
                <option value="">All banks</option>
                {banks.map((b: any) => (
                  <option key={b.id} value={b.id}>
                    {b.title}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={() => {
                setSubject('');
                setDifficulty('');
                setBankId('');
                setSearchText('');
                setPage(0);
              }}
              className="w-full text-xs text-blue-600 dark:text-blue-400 hover:underline pt-1"
            >
              Clear filters
            </button>
          </div>
        </aside>

        {/* Main content */}
        <div className="flex-1 space-y-4">
          {/* Search bar */}
          <div className="relative">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" />
            <input
              type="text"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setPage(0);
              }}
              placeholder="Search questions..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            />
          </div>

          {/* Table */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
            {isLoading ? (
              <div className="flex justify-center py-16">
                <ArrowPathIcon className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : questions.length === 0 ? (
              <div className="py-16 text-center text-gray-500 dark:text-gray-400 text-sm">
                No questions match your filters.
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>
                        {['ID', 'Subject', 'Difficulty', 'Question', 'Bank', 'Active', ''].map(
                          (h) => (
                            <th
                              key={h}
                              className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                            >
                              {h}
                            </th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {questions.map((q: any) => (
                        <tr key={q.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 font-mono">
                            {q.id}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                            {q.subject || '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${
                                q.difficulty === 'easy'
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                                  : q.difficulty === 'hard'
                                  ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                  : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'
                              }`}
                            >
                              {q.difficulty || 'medium'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-[320px] truncate">
                            {q.question_text || q.question || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                            {q.bank_title || q.bank_id || '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-block w-2.5 h-2.5 rounded-full ${
                                q.is_active !== false
                                  ? 'bg-green-500'
                                  : 'bg-gray-300 dark:bg-gray-600'
                              }`}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => openEdit(q)}
                              className="p-1.5 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400"
                              title="Edit"
                            >
                              <PencilSquareIcon className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {page * PAGE_SIZE + 1} - {Math.min((page + 1) * PAGE_SIZE, total)} of {total}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0}
                      className="p-1.5 rounded text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-30"
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                    </button>
                    <span className="text-xs text-gray-600 dark:text-gray-300 px-2">
                      {page + 1} / {totalPages}
                    </span>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                      className="p-1.5 rounded text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-30"
                    >
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editingQuestion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg mx-4 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Edit Question #{editingQuestion.id}
              </h3>
              <button
                onClick={() => setEditingQuestion(null)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Question Text
                </label>
                <textarea
                  value={editForm.question_text}
                  onChange={(e) =>
                    setEditForm((f: any) => ({ ...f, question_text: e.target.value }))
                  }
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={editForm.subject}
                    onChange={(e) =>
                      setEditForm((f: any) => ({ ...f, subject: e.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Difficulty
                  </label>
                  <select
                    value={editForm.difficulty}
                    onChange={(e) =>
                      setEditForm((f: any) => ({ ...f, difficulty: e.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Explanation
                </label>
                <textarea
                  value={editForm.explanation}
                  onChange={(e) =>
                    setEditForm((f: any) => ({ ...f, explanation: e.target.value }))
                  }
                  rows={2}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={editForm.is_active}
                  onChange={(e) =>
                    setEditForm((f: any) => ({ ...f, is_active: e.target.checked }))
                  }
                  className="rounded"
                />
                <label
                  htmlFor="is_active"
                  className="text-sm text-gray-700 dark:text-gray-300"
                >
                  Active
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setEditingQuestion(null)}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => editMutation.mutate()}
                disabled={editMutation.isPending}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {editMutation.isPending ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
